import { createProviders, type ErrorInfo } from '@his/shared';
import { Toast, toast } from '@heroui/react';
import { store } from '@/stores/store';
import { trpc, trpcClient, trpcProxyClient } from '@/lib/trpc';

/**
 * 应用根 Provider
 * 
 * ✅ 使用 shared 包的工厂函数创建 Providers
 * 
 * 组合了以下 Provider：
 * - tRPC Provider: API 请求
 * - React Query Provider: 数据缓存
 * - Redux Provider: 全局状态
 * - AuthProvider: 认证服务（用户信息获取 + 跨标签页同步）
 * - TokenRefreshHandler: Token 自动刷新
 * - Toaster: 错误提示（由业务模块提供）
 * 
 * 注意：移动端使用 localStorage + Header 认证（与 Web 端的 Cookie 认证不同）
 * 使用 HeroUI 作为 UI 框架（与 app 端统一）
 */
// 创建 Providers 组件
const BaseProviders = createProviders({
  store,
  trpc: trpc as any,
  trpcClient,
  trpcProxyClient,
  loginPath: '/login',
  isHomeApp: false,
  // 公开路径：不触发 auth.me、UNAUTHORIZED 静默；与 routes 里 requiresAuth: false 的活动子页保持一致
  publicPaths: ['/activities/items/olympic', '/activities/items/fcb', '/activities/items/fcb-second'],
  onError: (errorInfo: ErrorInfo) => {
    // 使用 HeroUI 的 toast 显示错误提示
    toast.danger('操作失败', {
      description: errorInfo.message,
      timeout: 3000,
    });
    if (import.meta.env.DEV) {
      console.error('[Error]', errorInfo.message, errorInfo.originalError);
    }
  },
});

// 导出 Providers 组件（包含 Toast.Provider）
export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <>
      <BaseProviders>{children}</BaseProviders>
      <Toast.Provider placement="top" />
    </>
  );
}

